/**
 * Cart inventory helpers.
 *
 * `/cart/get-all` returns per-line stock in `size`, keyed by size label:
 *   { "S": 10, "M": 15, "L": 8, "XL": 5 }
 * Each value is remaining stock for that size (max selectable qty).
 */

/** Show stock hint when addable remaining units are at or below this count. */
export const CART_STOCK_HINT_REMAINING_CAP = 10;

/** Use urgent (red) styling when addable remaining is at or below this count. */
export const CART_STOCK_HINT_URGENT_REMAINING_CAP = 5;

/** Remaining stock for a size from API `size` map (e.g. size.M → 15). */
export function getStockForSize(cartItem, size) {
    const sizeMap = cartItem?.size;
    const key = size ?? cartItem?.selectedSize;
    if (sizeMap && key != null && Object.prototype.hasOwnProperty.call(sizeMap, key)) {
        const n = Number(sizeMap[key]);
        return Number.isFinite(n) ? Math.max(0, n) : 0;
    }
    return 0;
}

export function getSelectedSizeStock(cartItem) {
    return getStockForSize(cartItem, cartItem?.selectedSize);
}

export function isCartLineOutOfStock(cartItem) {
    return getSelectedSizeStock(cartItem) <= 0;
}

export function partitionCartByStock(items) {
    const list = Array.isArray(items) ? items : [];
    const inStock = [];
    const outOfStock = [];
    list.forEach((item) => {
        if (isCartLineOutOfStock(item)) {
            outOfStock.push(item);
        } else {
            inStock.push(item);
        }
    });
    return { inStock, outOfStock };
}

/** Max selectable qty for the active size. */
export function getCartLineMaxStock(cartItem, size) {
    return getStockForSize(cartItem, size ?? cartItem?.selectedSize);
}

/** Units still addable: API stock for size minus current cart qty. */
export function getRemainingAddableStock(cartItem, size, selectedQty) {
    const stock = getStockForSize(cartItem, size);
    const qty = Number(selectedQty) || 1;
    return stock - qty;
}

/** Stock hint text, or null when hidden. */
export function getCartStockHint(cartItem, size, selectedQty) {
    const stock = getStockForSize(cartItem, size);
    if (stock <= 0) return null;

    const remaining = getRemainingAddableStock(cartItem, size, selectedQty);
    if (remaining < 0 || remaining > CART_STOCK_HINT_REMAINING_CAP) return null;

    if (remaining === 0) {
        return stock === 1 ? 'Last one' : '0 left';
    }
    return `${remaining} left`;
}

/** True when stock hint is visible and remaining addable qty is critically low. */
export function isCartStockHintUrgent(cartItem, size, selectedQty) {
    const stock = getStockForSize(cartItem, size);
    if (stock <= 0) return false;

    const remaining = getRemainingAddableStock(cartItem, size, selectedQty);
    if (remaining < 0 || remaining > CART_STOCK_HINT_REMAINING_CAP) return false;

    return remaining <= CART_STOCK_HINT_URGENT_REMAINING_CAP;
}

/** Qty options [1..stock] from API `size[selectedSize]`. */
export function getCartQuantityOptions(cartItem, size) {
    const max = getCartLineMaxStock(cartItem, size);
    if (max <= 0) return [1];
    return Array.from({ length: max }, (_, i) => i + 1);
}

export function nextCartQuantity(currentQty, delta, maxStock) {
    const max = Math.max(1, maxStock);
    const current = Number(currentQty) || 1;
    let next = current + delta;
    if (next < 1) {
        return { nextQty: null, removeItem: true };
    }
    if (delta > 0) {
        next = Math.min(next, max);
    }
    return { nextQty: next, removeItem: false };
}

export function clampQtyToStock(qty, maxStock) {
    const max = Math.max(1, maxStock);
    const q = Number(qty) || 1;
    return Math.min(Math.max(1, q), max);
}
